/* eslint-disable */
import React, { useState, useMemo } from 'react';
import StatusPill from './StatusPill';

/**
 * DeploymentShiftDetail — main working screen for a shift.
 * Shows allocation list + action tiles (scan out/in, swap, close).
 */
export default function DeploymentShiftDetail({ st, setSt, shiftId, account, audit, onBack, onScanOut, onScanIn, onSwap, onCloseShift }) {
  const [filter, setFilter] = useState('all');
  const shift = (st.shifts || []).find(s => s.id === shiftId);
  const allocs = useMemo(() => (st.allocations || []).filter(a => a.shiftId === shiftId), [st.allocations, shiftId]);

  if (!shift) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Shift not found</div>;

  // Auto-activate planned shifts past their start time
  const effectiveStatus = shift.status === 'planned' && new Date(shift.startAt) <= new Date() ? 'active' : shift.status;
  const isActive = effectiveStatus === 'active';
  const isClosed = effectiveStatus === 'closed' || effectiveStatus === 'closed_with_open_items';

  const outCount = allocs.filter(a => a.status === 'out').length;
  const returnedCount = allocs.filter(a => a.status === 'returned').length;
  const totalCount = allocs.length;

  const filtered = filter === 'all' ? allocs
    : filter === 'out' ? allocs.filter(a => a.status === 'out')
    : allocs.filter(a => a.status === 'returned');

  const sorted = [...filtered].sort((a, b) => new Date(b.outAt) - new Date(a.outAt));

  const fmtTime = (d) => d ? new Date(d).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '—';
  const fmtDate = (d) => new Date(d).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });

  // Start shift action
  const handleStartShift = () => {
    setSt(prev => ({
      ...prev,
      shifts: prev.shifts.map(s => s.id === shiftId ? { ...s, status: 'active', startedAt: new Date().toISOString() } : s)
    }));
    audit('shift_started', { shiftId, title: shift.title });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      {/* Header */}
      <div style={{ padding: '8px 20px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'var(--surface-app)', borderBottom: '1px solid var(--border-subtle)', flexShrink: 0 }}>
        <button onClick={onBack} style={{ width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', border: '1px solid var(--border-default)', borderRadius: '50%', color: 'var(--text-muted)', cursor: 'pointer' }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M15 18l-6-6 6-6"/></svg>
        </button>
        <div style={{ textAlign: 'center', flex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-default)' }}>Shift {shift.shiftNumber} · {fmtDate(shift.startAt)}{shift.anonymousMode && <span style={{ marginLeft: 6, padding: '2px 6px', background: 'rgba(191,128,255,0.15)', color: '#bf80ff', borderRadius: 4, fontSize: 9, fontWeight: 700, letterSpacing: '0.04em', verticalAlign: 'middle' }}>ANON</span>}</div>
          <div style={{ fontSize: 11, color: 'var(--text-subtle)', marginTop: 2 }}>{shift.possessionRef} · {fmtTime(shift.startAt)} → {fmtTime(shift.endAt)}</div>
        </div>
        {isActive && !isClosed && (
          <button onClick={onCloseShift} style={{ minWidth: 36, height: 36, padding: '0 10px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', border: '1px solid var(--border-default)', borderRadius: 18, color: 'var(--text-muted)', cursor: 'pointer', fontSize: 12, fontWeight: 500 }}>Close</button>
        )}
        {!isActive && !isClosed && (
          <button onClick={handleStartShift} style={{ minWidth: 36, height: 36, padding: '0 10px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#ff530d', border: 'none', borderRadius: 18, color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>Start</button>
        )}
        {isClosed && <div style={{ width: 36 }} />}
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
        {/* Closed banner */}
        {isClosed && (
          <div style={{ background: 'var(--surface-raised)', border: '1px solid var(--border-default)', borderRadius: 10, padding: '10px 14px', fontSize: 12, color: 'var(--text-muted)', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
            This shift is closed. Allocations are read-only.
          </div>
        )}

        {/* Action tiles */}
        {isActive && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 18 }}>
            <button onClick={onScanOut} style={{ padding: '16px 12px', background: '#ff530d', border: 'none', borderRadius: 14, textAlign: 'center', cursor: 'pointer', color: '#fff', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 7V5a2 2 0 0 1 2-2h2"/><path d="M17 3h2a2 2 0 0 1 2 2v2"/><path d="M21 17v2a2 2 0 0 1-2 2h-2"/><path d="M7 21H5a2 2 0 0 1-2-2v-2"/><rect x="7" y="7" width="10" height="10" rx="1"/></svg>
              <span style={{ fontSize: 13, fontWeight: 600 }}>Scan Out</span>
            </button>
            <button onClick={onScanIn} style={{ padding: '16px 12px', background: 'var(--surface-default)', border: '1px solid var(--border-subtle)', borderRadius: 14, textAlign: 'center', cursor: 'pointer', color: 'var(--text-default)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 12l2 2 4-4"/><rect x="3" y="3" width="18" height="18" rx="2"/></svg>
              <span style={{ fontSize: 13, fontWeight: 600 }}>Scan In</span>
            </button>
            <button onClick={onSwap} style={{ gridColumn: '1 / -1', padding: '14px', background: 'var(--surface-default)', border: '1px solid var(--border-subtle)', borderRadius: 14, textAlign: 'center', cursor: 'pointer', color: 'var(--text-default)', display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M7 16V4M3 8l4-4 4 4"/><path d="M17 8v12m4-4l-4 4-4-4"/></svg>
              <span style={{ fontSize: 13, fontWeight: 600 }}>Swap device</span>
            </button>
          </div>
        )}

        {/* Filter tabs */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 14, padding: 4, background: 'var(--surface-default)', borderRadius: 10, border: '1px solid var(--border-subtle)' }}>
          {[
            { key: 'all', label: 'All', count: totalCount },
            { key: 'out', label: 'Out', count: outCount },
            { key: 'returned', label: 'Returned', count: returnedCount },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setFilter(tab.key)}
              style={{
                flex: 1, padding: 8, background: filter === tab.key ? 'var(--surface-raised)' : 'transparent',
                border: 'none', color: filter === tab.key ? 'var(--text-default)' : 'var(--text-muted)',
                fontFamily: 'inherit', fontSize: 12, fontWeight: 500, borderRadius: 6, cursor: 'pointer',
              }}
            >
              {tab.label} <span style={{ marginLeft: 4, color: 'var(--text-subtle)', fontSize: 11, fontVariantNumeric: 'tabular-nums' }}>{tab.count}</span>
            </button>
          ))}
        </div>

        {/* Allocation list */}
        {sorted.length === 0 && (
          <div style={{ padding: '30px 20px', textAlign: 'center', color: 'var(--text-subtle)' }}>
            <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-muted)', marginBottom: 4 }}>
              {filter === 'all' ? 'No allocations yet' : `No ${filter} allocations`}
            </div>
            <div style={{ fontSize: 11 }}>{filter === 'all' ? 'Scan a device out to get started.' : ''}</div>
          </div>
        )}

        {sorted.map(alloc => {
          const device = (st.devices || []).find(d => d.id === alloc.deviceId);
          const isReturned = alloc.status === 'returned' || alloc.status === 'carried_over';
          const isFlagged = alloc.status === 'flagged_missing';

          return (
            <div
              key={alloc.id}
              style={{
                background: 'var(--surface-default)', border: '1px solid var(--border-subtle)',
                borderRadius: 10, padding: '12px 14px', marginBottom: 8,
                display: 'flex', alignItems: 'center', gap: 12,
                opacity: isReturned ? 0.6 : 1,
                transition: 'all 0.15s ease',
              }}
            >
              <div style={{ minWidth: 60 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-default)', fontVariantNumeric: 'tabular-nums' }}>
                  {alloc.anonDeviceLabel || device?.serial?.replace(/^.*-(\d+)-\d+$/, '$1') || alloc.deviceId?.slice(-4) || '?'}
                </div>
                <div style={{ fontSize: 10, color: 'var(--text-subtle)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  {device?.type || ''}
                </div>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-default)', marginBottom: 2 }}>
                  {alloc.role}
                  {alloc.swappedFromAllocationId && <span style={{ fontSize: 10, color: '#60a5fa', marginLeft: 4 }}>from swap</span>}
                  {alloc.swappedToAllocationId && <span style={{ fontSize: 10, color: '#60a5fa', marginLeft: 4 }}>→ swapped</span>}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {alloc.operativeName || <span style={{ color: 'var(--text-subtle)' }}>Unassigned plant</span>}
                  {alloc.operativePhone && ` · ${alloc.operativePhone}`}
                </div>
              </div>
              <div style={{ textAlign: 'right', fontSize: 10, color: 'var(--text-subtle)', fontVariantNumeric: 'tabular-nums' }}>
                <span style={{ fontSize: 12, color: isReturned ? '#4ade80' : isFlagged ? '#f87171' : 'var(--text-default)', fontWeight: 500, display: 'block' }}>
                  {isReturned ? fmtTime(alloc.inAt) : fmtTime(alloc.outAt)}
                </span>
                <span>{isReturned ? 'Returned' : isFlagged ? 'Flagged' : 'Out'}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
